/**
 * BONIFAKTUR Boni-Points — loyalty page member dashboard.
 *
 * Talks to the loyalty Worker through the App Proxy at /apps/loyalty:
 *   GET  /apps/loyalty         -> { balance, catalog, codes, logged_in }
 *   POST /apps/loyalty/redeem  -> { code, apply_url, balance }
 *
 * Liquid first-paints balance/ring/activity from theme-readable metafields;
 * this hydrates with the authoritative balance + catalog + codes, drives the
 * redeem flow, and keeps the progress ring + "to next tier" line in sync.
 * Vanilla JS. UI strings come from a Liquid JSON blob ([data-boni-i18n]).
 */
(function () {
  var PROXY = '/apps/loyalty';

  // Redemption ladder — keep in sync with the loyalty_reward catalog.
  var TIERS = [{ at: 1200, val: 5 }, { at: 2500, val: 10 }];

  var redeemRoot = document.querySelector('[data-boni-redeem]');
  var balanceEl = document.querySelector('[data-boni-balance]');
  var cardsEl = document.querySelector('[data-boni-cards]');
  var codesEl = document.querySelector('[data-boni-codes]');
  var ringEl = document.querySelector('[data-boni-ring]');
  var ringLabelEl = document.querySelector('[data-boni-ring-label]');
  var nextEl = document.querySelector('[data-boni-next]');
  var pendingWrap = document.querySelector('[data-boni-pending-wrap]');
  var pendingEl = document.querySelector('[data-boni-pending]');

  // Nothing to do on the guest view (no redeem root rendered).
  if (!redeemRoot && !cardsEl) return;

  var i18n = readI18n();
  var loggedIn = redeemRoot && redeemRoot.getAttribute('data-logged-in') === 'true';
  var state = { balance: 0, pending: 0, catalog: [], codes: [] };

  load();

  function load() {
    fetch(PROXY, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        state.balance = d.balance || 0;
        state.pending = d.pending || 0;
        state.catalog = d.catalog || [];
        state.codes = d.codes || [];
        loggedIn = !!d.logged_in;
        render();
      })
      .catch(function () { /* keep the Liquid first-paint */ });
  }

  function render() {
    if (balanceEl) balanceEl.textContent = formatNum(state.balance);
    renderPending();
    renderRing();
    renderCards();
    renderCodes();
  }

  // Pending points: queued but not yet awarded (credited ~21 days after
  // payment). Hidden when zero. Never folded into the redeemable balance —
  // the ring/tier math below uses state.balance only.
  function renderPending() {
    if (pendingEl) pendingEl.textContent = formatNum(state.pending);
    if (pendingWrap) pendingWrap.hidden = !(state.pending > 0);
  }

  // Progress ring + "noch X Punkte bis Y %"
  function renderRing() {
    var prev = 0, next = null, nextVal = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (state.balance >= TIERS[i].at) { prev = TIERS[i].at; }
      else { next = TIERS[i].at; nextVal = TIERS[i].val; break; }
    }
    var pct = next ? Math.round(((state.balance - prev) * 100) / (next - prev)) : 100;
    if (ringEl) ringEl.style.setProperty('--pct', pct);
    if (ringLabelEl) ringLabelEl.textContent = pct;
    if (nextEl) {
      nextEl.textContent = next
        ? tpl(i18n.to_next, { points: formatNum(next - state.balance), value: nextVal })
        : i18n.all_unlocked;
    }
  }

  function renderCards() {
    if (!cardsEl) return;
    cardsEl.innerHTML = '';
    state.catalog.forEach(function (r) {
      var affordable = state.balance >= r.cost_points;
      var card = document.createElement('article');
      card.className = 'boni-reward' + (affordable && loggedIn ? '' : ' boni-reward--locked');

      var title = document.createElement('h3');
      title.className = 'boni-reward__title t-h3';
      title.textContent = r.title;

      var cost = document.createElement('span');
      cost.className = 'boni-reward__cost t-mono';
      cost.textContent = formatNum(r.cost_points) + ' ' + i18n.points;

      var btn = document.createElement('button');
      btn.className = 'btn btn--primary boni-reward__btn';
      btn.type = 'button';

      if (!loggedIn) {
        btn.textContent = i18n.login_to_redeem;
        btn.addEventListener('click', function () { window.location.href = '/account/login'; });
      } else if (affordable) {
        btn.textContent = i18n.redeem_btn;
        btn.addEventListener('click', function () { redeem(r, btn); });
      } else {
        btn.textContent = tpl(i18n.points_to_go, { points: formatNum(r.cost_points - state.balance), label: r.title });
        btn.disabled = true;
      }

      card.appendChild(title);
      card.appendChild(cost);
      card.appendChild(btn);
      cardsEl.appendChild(card);
    });
  }

  function redeem(reward, btn) {
    var ok = window.confirm(tpl(i18n.confirm, { points: formatNum(reward.cost_points), label: reward.title }));
    if (!ok) return;
    btn.disabled = true;
    btn.textContent = i18n.redeeming;
    fetch(PROXY + '/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ reward_id: reward.id })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          window.alert(res.d && res.d.error === 'Insufficient points' ? i18n.insufficient : i18n.error);
          render();
          return;
        }
        state.balance = res.d.balance;
        state.codes.unshift({
          title: reward.title, code: res.d.code, type: reward.type,
          value: reward.value, redeemed_at: new Date().toISOString(), used: false
        });
        render();
        if (codesEl) codesEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      })
      .catch(function () { window.alert(i18n.error); render(); });
  }

  // Codes split: active (usable) vs used. `used` comes from the Worker when it
  // enriches codes with usage; until then everything renders as active.
  function renderCodes() {
    if (!codesEl) return;
    codesEl.innerHTML = '';
    if (!state.codes.length) return;

    var active = state.codes.filter(function (c) { return !c.used; });
    var used = state.codes.filter(function (c) { return c.used; });

    if (active.length) codesEl.appendChild(codeGroup(i18n.your_codes, active, false));
    if (used.length) codesEl.appendChild(codeGroup(i18n.your_codes_used, used, true));
  }

  function codeGroup(titleText, list, isUsed) {
    var frag = document.createDocumentFragment();
    var h = document.createElement('h3');
    h.className = 'boni-codes__title t-mono';
    h.textContent = titleText;
    frag.appendChild(h);

    list.forEach(function (c) {
      var row = document.createElement('div');
      row.className = 'boni-code' + (isUsed ? ' boni-code--used' : '');

      var label = document.createElement('span');
      label.className = 'boni-code__label';
      label.textContent = c.title;

      var codeEl = document.createElement('code');
      codeEl.className = 'boni-code__code';
      codeEl.textContent = c.code;

      row.appendChild(label);
      row.appendChild(codeEl);

      if (!isUsed) {
        var apply = document.createElement('a');
        apply.className = 'boni-code__apply';
        apply.href = '/discount/' + encodeURIComponent(c.code) + '?redirect=/cart';
        apply.textContent = i18n.apply;

        var copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'boni-code__copy t-mono';
        copy.textContent = i18n.copy;
        copy.addEventListener('click', function () {
          if (navigator.clipboard) navigator.clipboard.writeText(c.code);
          copy.textContent = i18n.copied;
          setTimeout(function () { copy.textContent = i18n.copy; }, 1500);
        });

        row.appendChild(apply);
        row.appendChild(copy);
      }
      frag.appendChild(row);
    });

    var wrap = document.createElement('div');
    wrap.className = 'boni-codes__group';
    wrap.appendChild(frag);
    return wrap;
  }

  function readI18n() {
    var el = document.querySelector('[data-boni-i18n]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }
  function tpl(s, vars) {
    return (s || '').replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
  }
  function formatNum(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
})();
